import React, { useEffect, useState, useCallback } from 'react';
import { XIcon, DownloadIcon, ArrowDownToLineIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { FREE_DOWNLOADS, FreeDownload } from '../../data/site';

/*  THE CRATE — hidden free-downloads section (Ryan, Aug 29).
    Not in the page's scroll/snap flow: it's a full-screen overlay that opens from
    the FREE DOWNLOADS button in the Records section, or by linking straight to
    futureastronautco.com/#downloads (hash-driven, so back button closes it).
    Index 0 of FREE_DOWNLOADS is the featured latest drop; the rest render as a
    HORIZONTAL LIST — one row per download (Ryan, Aug 29), not a card grid.
    Every row points at the owned download gate — WAV + cover come off the
    ClickUp release task; the gate URL goes in the `gate` field. */

const OPEN_HASH = '#downloads';
function useCrateOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const sync = () => setOpen(window.location.hash === OPEN_HASH);
    sync();
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, []);
  const close = useCallback(() => {
    if (window.location.hash === OPEN_HASH) {
      history.pushState('', document.title, window.location.pathname + window.location.search);
      setOpen(false);
    }
  }, []);
  return {
    open,
    close
  };
}
function GateButton({
  item,
  big = false
}: {
  item: FreeDownload;
  big?: boolean;
}) {
  return <a href={item.gate} target="_blank" rel="noopener noreferrer" className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-violet font-tech font-semibold tracking-[0.14em] text-white shadow-neon transition-[transform,background-color] duration-200 ease-expo hover:scale-[1.03] hover:bg-violet-soft ${big ? 'px-8 py-4 text-sm' : 'px-4 py-2.5 text-[0.66rem] sm:px-5'}`} data-id="element-117" data-mp="vsrveq4mkc5">
      <DownloadIcon className={big ? 'h-4 w-4' : 'h-3.5 w-3.5'} aria-hidden="true" data-id="element-118" data-mp="jnbrdbkxzg9f" />
      <span className={big ? '' : 'hidden sm:inline'} data-id="element-119" data-mp="2fqq9yi4u29qq">FREE DOWNLOAD</span>
      <span className={big ? 'hidden' : 'sm:hidden'} data-id="element-120" data-mp="2bpejkae4xkhh">GET</span>
    </a>;
}
export function Downloads() {
  const {
    open,
    close
  } = useCrateOpen();

  // lock the page scroll while the crate is open
  useEffect(() => {
    if (!open) return;
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.documentElement.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  const [latest, ...rest] = FREE_DOWNLOADS;
  return <AnimatePresence data-id="element-121" data-mp="1e1wa0xcngecf">
      {open ? <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-0 z-[70] overflow-y-auto bg-void" role="dialog" aria-modal="true" aria-label="Free downloads" data-id="element-122" data-mp="1e2xmn69bd8w9">
          {/* nebula wash */}
          <div aria-hidden="true" className="pointer-events-none fixed inset-0" style={{
        background: 'radial-gradient(55% 45% at 15% 0%, rgba(139,92,255,0.28) 0%, rgba(4,3,12,0) 100%), radial-gradient(50% 45% at 95% 100%, rgba(55,229,255,0.14) 0%, rgba(4,3,12,0) 100%)'
      }} data-id="element-123" data-mp="3bwjkktxcskwg" />

          <div className="relative mx-auto w-full max-w-5xl px-5 pb-24 pt-8 sm:px-8" data-id="element-124" data-mp="2syphsxmtj5mi">
            {/* top bar */}
            <div className="flex items-center justify-between" data-id="element-125" data-mp="2x0annz0xqjn7">
              <p className="font-tech text-[0.68rem] font-semibold tracking-[0.3em] text-cyan/85" data-id="element-126" data-mp="r6ua0t3onl9k">
                FUTURE ASTRONAUT RECORDS
              </p>
              <button type="button" onClick={close} aria-label="Close free downloads" className="glass flex h-10 w-10 items-center justify-center rounded-full text-white transition-colors duration-200 ease-out hover:border-violet/60" data-id="element-127" data-mp="v7cz4fe6yd82">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-128" data-mp="3sqjh9xuzc8wa" />
              </button>
            </div>

            <h2 className="mt-8 font-display text-2xl tracking-[0.06em] text-white sm:text-3xl" data-id="element-129" data-mp="3nrzuxwch4v3e">
              Free <span className="text-gradient" data-id="element-130" data-mp="3exbnbzjff1bf">downloads</span>
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/55" data-id="element-131" data-mp="3ev8mslkndpsg">
              Weapons for your sets. Grab the files free — each one unlocks with a quick
              SoundCloud follow + repost.
            </p>

            {latest ? <>
                {/* featured latest drop — horizontal hero row */}
                <div className="glass mt-10 flex flex-col gap-5 rounded-2xl p-5 sm:flex-row sm:items-center sm:gap-6" data-id="element-132" data-mp="2vxxq98qehagp">
                  <img src={latest.cover} alt={`${latest.title} cover art`} className="aspect-square w-full rounded-xl border border-white/10 object-cover sm:w-44 md:w-52" loading="eager" data-id="element-133" data-mp="1bfdgprfy7b8s" />
                  <div className="min-w-0 flex-1" data-id="element-134" data-mp="1pgg3k6eropkr">
                    <p className="font-tech text-[0.65rem] font-semibold tracking-[0.3em] text-cyan/85" data-id="element-135" data-mp="1pe3mdvgu2j8r">
                      LATEST DROP
                    </p>
                    <p className="mt-2.5 font-tech text-sm font-semibold tracking-[0.14em] text-white/85" data-id="element-136" data-mp="wsaifijswyke">
                      {latest.artist.toUpperCase()}
                    </p>
                    <h3 className="mt-1 font-display text-lg text-white sm:text-xl" data-id="element-137" data-mp="16hlmurvqtf1y">{latest.title}</h3>
                    {latest.subgenre ? <span className="mt-3 inline-block rounded-full border border-violet/50 bg-violet/10 px-3 py-1 font-tech text-[0.62rem] tracking-[0.2em] text-violet-soft" data-id="element-138" data-mp="8zu0d802d6l3">
                        {latest.subgenre.toUpperCase()}
                      </span> : null}
                    <div className="mt-5" data-id="element-139" data-mp="1yhrl1eq6w1i">
                      <GateButton item={latest} big data-id="element-140" data-mp="fpv620xs13po" />
                    </div>
                  </div>
                </div>

                {/* the rest of the crate — horizontal list rows */}
                {rest.length > 0 ? <div className="mt-8 flex flex-col gap-3" data-id="element-141" data-mp="3j7mvp8ofxbkp">
                    {rest.map(item => <div key={item.code} className="glass group flex items-center gap-4 rounded-2xl p-3 pr-3.5 transition-[transform,border-color,box-shadow] duration-300 ease-expo hover:-translate-y-0.5 hover:border-violet/50 hover:shadow-neon sm:p-3.5 sm:pr-4" data-id="element-142" data-mp="3f6l33ap13xrp">
                        <img src={item.cover} alt={`${item.title} cover art`} className="h-16 w-16 shrink-0 rounded-lg border border-white/10 object-cover sm:h-20 sm:w-20" loading="lazy" data-id="element-143" data-mp="18gn2mzir7udj" />
                        <div className="min-w-0 flex-1" data-id="element-144" data-mp="2ccxiv1peywuq">
                          <p className="truncate font-tech text-[0.62rem] font-semibold tracking-[0.12em] text-white/70" data-id="element-145" data-mp="2ceib2ko9a3pf">
                            {item.artist.toUpperCase()}
                          </p>
                          <p className="mt-0.5 truncate text-sm font-medium text-white sm:text-[0.95rem]" data-id="element-146" data-mp="353fuir47e6jr">
                            {item.title}
                          </p>
                          {item.subgenre ? <span className="mt-1.5 hidden rounded-full border border-white/15 px-2.5 py-0.5 font-tech text-[0.58rem] tracking-[0.18em] text-white/50 sm:inline-block" data-id="element-147" data-mp="30ammnudfui3g">
                              {item.subgenre.toUpperCase()}
                            </span> : null}
                        </div>
                        <GateButton item={item} data-id="element-148" data-mp="1spg1ns3je3lm" />
                      </div>)}
                  </div> : null}
              </> : (/* empty state — shows until the first row lands in FREE_DOWNLOADS */
        <div className="glass mt-10 flex flex-col items-center rounded-2xl px-6 py-16 text-center" data-id="element-149" data-mp="1e01qqfkbbe44">
                <ArrowDownToLineIcon className="h-8 w-8 text-violet-soft" aria-hidden="true" data-id="element-150" data-mp="2bo20t1fhtkmw" />
                <p className="mt-4 font-display text-lg text-white" data-id="element-151" data-mp="26nxkpcihyrx7">First drops loading…</p>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-white/55" data-id="element-152" data-mp="21p2sxl2jxt0y">
                  The free download crate opens with our first releases. Follow the label on
                  SoundCloud so you don't miss the drop.
                </p>
                <a href="https://soundcloud.com/futureastronautco" target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-violet px-6 py-3 font-tech text-xs font-semibold tracking-[0.16em] text-white shadow-neon transition-[transform,background-color] duration-200 ease-expo hover:scale-[1.03] hover:bg-violet-soft" data-id="element-153" data-mp="qbqs7j239qj0">
                  FOLLOW ON SOUNDCLOUD
                </a>
              </div>)}
          </div>
        </motion.div> : null}
    </AnimatePresence>;
}